import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material'
import {
  CommonTextField,
  CommonButton,
  CommonDialog,
} from '@/components/common'
import { useChairpersonContext } from '@/context'
import { useChairpersonResetPassword } from '@/hooks'
import { VisibilityIcon, VisibilityOffIcon } from '@/icons'
import { IconSuccess } from '@/assets/icons'

/**
 * Reusable chairperson reset password form.
 * @param {Object} props
 * @param {'page' | 'dialog'} props.variant - 'page' = full card layout, 'dialog' = compact for modal
 * @param {boolean} [props.showRequiredMessage=true] - Show "You must set a new password to continue"
 * @param {() => void | Promise<void>} props.onSuccess - Called after user confirms success (chairperson is refreshed first)
 */
export const ChairpersonResetPasswordForm = ({
  variant = 'page',
  showRequiredMessage = true,
  onSuccess,
}) => {
  const { chairperson, getChairperson } = useChairpersonContext()
  const { loadingResetPassword, resetPassword } = useChairpersonResetPassword()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleResetPassword = async () => {
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    try {
      await resetPassword({ password: newPassword })
      setSuccess(true)
      setError('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.')
    }
  }

  const handleSuccessDialogClose = async () => {
    setSuccess(false)
    await getChairperson()
    await onSuccess?.()
  }

  const displayEmail = chairperson?.email || chairperson?.accounts?.[0]?.email
  const isDialog = variant === 'dialog'

  const formContent = (
    <Box
      component='form'
      onSubmit={e => {
        e.preventDefault()
        handleResetPassword()
      }}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {!isDialog && (
        <Typography
          variant='h4'
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          Reset your password
        </Typography>
      )}
      {displayEmail && (
        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Box component='span' sx={{ color: 'primary.main' }}>
            {displayEmail}
          </Box>
        </Typography>
      )}
      {showRequiredMessage && (
        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          You must set a new password to continue.
        </Typography>
      )}
      <CommonTextField
        id='chairperson-new-password'
        label='New Password'
        value={newPassword}
        setValue={setNewPassword}
        type={showNewPassword ? 'text' : 'password'}
        error={error}
        placeholder='Enter new password (min 8 characters)'
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle new password visibility'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  onMouseDown={e => e.preventDefault()}
                  edge='end'
                  size='small'
                >
                  {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <CommonTextField
        id='chairperson-confirm-password'
        label='Confirm Password'
        value={confirmPassword}
        setValue={setConfirmPassword}
        type={showConfirmPassword ? 'text' : 'password'}
        error={error}
        placeholder='Confirm new password'
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle confirm password visibility'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  onMouseDown={e => e.preventDefault()}
                  edge='end'
                  size='small'
                >
                  {showConfirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <CommonButton
        id='chairperson-reset-password-button'
        label='Continue'
        onClick={handleResetPassword}
        loading={loadingResetPassword}
        disabled={!newPassword || !confirmPassword}
        color='primary'
        variant='primary'
        size='large'
        style={{ width: '100%' }}
      />
    </Box>
  )

  return (
    <>
      {isDialog ? (
        formContent
      ) : (
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            width: '100%',
            maxWidth: 540,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {formContent}
        </Paper>
      )}

      <CommonDialog
        open={success}
        onClose={handleSuccessDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={IconSuccess} alt='success' />
          </Box>
          <Box textAlign='center' mt={2}>
            <Typography variant='h6' color='primary'>
              Success
            </Typography>
            <Typography variant='body1' sx={{ my: 3 }}>
              Your password has been reset successfully
            </Typography>
            <CommonButton
              variant='primary'
              onClick={handleSuccessDialogClose}
              sx={{ px: 4 }}
            >
              Ok
            </CommonButton>
          </Box>
        </Box>
      </CommonDialog>
    </>
  )
}
